import { useId, useMemo, useState, type ChangeEvent } from 'react';
import type { TFunction } from 'i18next';
import type { ArtifactKind } from '@inference.engine/widgets';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  resolveWorkspaceArtifactSort,
  workspaceArtifactsInfiniteQuery,
  type WorkspaceArtifactRow,
} from '@/entities/artifact';
import type { CreateBriefTemplateVariables } from '@/entities/brief-template';
import { usePermissions } from '@/entities/user';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { SEARCH_DEBOUNCE_MS } from '@/shared/constants/search.constants';
import { useDebounce } from '@/shared/hooks';

import { useBriefTemplateIntakeForm } from '../../model/useBriefTemplateIntakeForm';
import { useTemplateGeneration } from '../../model/useTemplateGeneration/useTemplateGeneration';

import type { BriefArtifactRef } from '../../model/brief-template-intake.types';
import type { BriefTemplateIntakeSheetProps } from './BriefTemplateIntakeSheet.types';

type Step = 'form' | 'picker';
export type BriefTemplateCreateLevel = NonNullable<CreateBriefTemplateVariables['level']>;
export type BriefTemplateStartMode = 'generate' | 'blank';

/**
 * All state, derived values, and handlers behind the brief template intake flow — shared verbatim
 * by the Dialog and Sheet shells so the form/picker/generation logic exists exactly once regardless
 * of which chrome renders it.
 *
 * Callers must mount only while `open` is true — every field seeds from scratch, so unmounting on
 * close is what resets the flow.
 */
export function useBriefTemplateIntakeShell({
  onClose,
  onCreated,
}: Pick<BriefTemplateIntakeSheetProps, 'onClose' | 'onCreated'>) {
  const form = useBriefTemplateIntakeForm();
  const fileInputId = useId();
  const titleFieldId = useId();
  const instructionsFieldId = useId();

  const [step, setStep] = useState<Step>('form');
  // Context, files and artifacts stay in the form while Start blank hides them, so switching back
  // to Generate with AI finds them as they were. Start blank never sends them.
  const [startMode, setStartMode] = useState<BriefTemplateStartMode>('generate');
  const isBlank = startMode === 'blank';
  const [chosenLevel, setLevel] = useState<BriefTemplateCreateLevel>('workspace');
  const levelsEnabled = useBooleanFlagValue('brief_template_levels', false);
  const permissions = usePermissions();
  // Flag off, creation is gated upstream exactly as before and always makes a workspace template.
  const canCreateWorkspace = !levelsEnabled || permissions.canManageWorkspaceBriefTemplates;
  const canCreateOrganization = levelsEnabled && permissions.canManageOrganizationBriefTemplates;
  // Shown whenever an organization template is possible; locked on Organization when that is the
  // only level the caller may create, so they see it will reach every workspace before creating.
  const canChooseLevel = canCreateOrganization;
  const levelChoiceLocked = !canCreateWorkspace;
  const level: BriefTemplateCreateLevel = canCreateWorkspace ? chosenLevel : 'organization';

  // Picker state lifted so Back → Browse preserves the user's input.
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerSelectedIds, setPickerSelectedIds] = useState<Set<string>>(() => new Set());
  const [pickerKindFilter, setPickerKindFilter] = useState<ArtifactKind | null>(null);
  const [pickerSort, setPickerSort] = useState<string | null>(null);

  const workspaceId = useCurrentWorkspaceId();

  // Subscribe to the same workspace-artifacts cache the picker reads so we
  // can map selectedIds → BriefArtifactRef[] on Add without a second fetch.
  const debouncedPickerSearch = useDebounce(pickerSearch, SEARCH_DEBOUNCE_MS);
  const trimmedPickerSearch = debouncedPickerSearch.trim();
  const pickerSortParams = resolveWorkspaceArtifactSort(pickerSort);

  const { data: pickerCacheData } = useInfiniteQuery(
    workspaceArtifactsInfiniteQuery(workspaceId ?? undefined, {
      kind: pickerKindFilter ?? undefined,
      search: trimmedPickerSearch || undefined,
      sortBy: pickerSortParams?.sortBy,
      sortOrder: pickerSortParams?.sortOrder,
    }),
  );

  const pickerLoadedRows = useMemo<WorkspaceArtifactRow[]>(
    () =>
      (pickerCacheData?.pages.flatMap((p) => p.artifacts) ??
        []) as unknown as WorkspaceArtifactRow[],
    [pickerCacheData],
  );

  const emptyAttachedIds = useMemo(() => new Set<string>(), []);

  const generation = useTemplateGeneration({
    onSuccess: (templateId) => onCreated(templateId),
  });

  const isGenerating =
    generation.status === 'uploading' ||
    generation.status === 'streaming' ||
    generation.status === 'persisting';

  if (generation.status === 'error') {
    generation.reset();
  }

  const handleOpenChange = (next: boolean) => {
    if (isGenerating) return;
    if (!next) onClose();
  };

  const handleGenerate = () => {
    if (!form.isValid || isGenerating) return;
    const payload = form.getSubmitPayload();
    void generation.start(
      canCreateOrganization && level === 'organization' ? { ...payload, level } : payload,
    );
  };

  const handleCreateBlank = () => {
    if (!form.isValid || isGenerating) return;
    const name = form.values.title.trim();
    generation.createBlank(
      canCreateOrganization && level === 'organization' ? { name, level } : { name },
    );
  };

  const handleFileInput = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return;
    form.addFiles(Array.from(event.target.files));
    // Reset so the user can re-attach the same file later.
    event.target.value = '';
  };

  const triggerUpload = () => {
    document.getElementById(fileInputId)?.click();
  };

  const handlePickerBack = () => {
    setStep('form');
  };

  const handlePickerAdd = () => {
    if (pickerSelectedIds.size === 0) return;
    const refs: BriefArtifactRef[] = pickerLoadedRows
      .filter((row) => pickerSelectedIds.has(row.id))
      .map((row) => ({
        id: row.id,
        name: row.name,
        kind: row.kind,
      }));
    form.addArtifacts(refs);
    // Reset selection but preserve the user's search filter for next Browse.
    setPickerSelectedIds(new Set());
    setStep('form');
  };

  const hasAttachments = form.values.files.length > 0 || form.values.artifacts.length > 0;
  // The picker only adds generation context, so Start blank never shows it.
  const isPickerStep = step === 'picker' && !isBlank;
  const isFormStep = !isPickerStep;

  const pickerSelectedCount = pickerSelectedIds.size;

  return {
    form,
    canChooseLevel,
    levelChoiceLocked,
    level,
    setLevel,
    startMode,
    setStartMode,
    isBlank,
    fileInputId,
    titleFieldId,
    instructionsFieldId,
    workspaceId,
    pickerSearch,
    setPickerSearch,
    pickerSelectedIds,
    setPickerSelectedIds,
    pickerKindFilter,
    setPickerKindFilter,
    pickerSort,
    setPickerSort,
    pickerLoadedRows,
    emptyAttachedIds,
    generation,
    isGenerating,
    isFormStep,
    isPickerStep,
    hasAttachments,
    pickerSelectedCount,
    handleOpenChange,
    handleGenerate,
    handleCreateBlank,
    handleFileInput,
    triggerUpload,
    setStep,
    handlePickerBack,
    handlePickerAdd,
  };
}

export type BriefTemplateIntakeShell = ReturnType<typeof useBriefTemplateIntakeShell>;

interface BriefTemplateIntakeFooterAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
}

/**
 * Derives the sheet/dialog title and the primary/secondary footer actions from the shell's current
 * step — pure function of state, so both shells resolve the same title and footer regardless of
 * which chrome renders them. `t` is passed in rather than called with `useTranslation` here so this
 * stays a plain function, callable from render without adding a hook rule violation.
 */
export function getBriefTemplateIntakeFooterActions(
  shell: BriefTemplateIntakeShell,
  t: TFunction<'briefs'>,
  onClose: () => void,
): {
  title: string;
  primary: BriefTemplateIntakeFooterAction;
  secondary: BriefTemplateIntakeFooterAction;
} {
  const title =
    shell.isPickerStep && !shell.isGenerating
      ? t('briefTemplateCreation.briefTemplateIntakeModal.addExistingArtifacts')
      : t('briefTemplateCreation.briefTemplateIntakeModal.newBriefTemplate');

  const submitLabel = shell.isBlank
    ? t('briefTemplateCreation.briefTemplateIntakeModal.createTemplate')
    : t('briefTemplateCreation.briefTemplateIntakeModal.generate');

  if (shell.isGenerating) {
    return {
      title,
      primary: {
        label: submitLabel,
        onClick: () => undefined,
        loading: true,
      },
      secondary: {
        label: t('briefTemplateCreation.briefTemplateIntakeModal.cancel'),
        onClick: () => undefined,
      },
    };
  }

  if (shell.isFormStep) {
    return {
      title,
      primary: {
        label: submitLabel,
        onClick: shell.isBlank ? shell.handleCreateBlank : shell.handleGenerate,
        disabled: !shell.form.isValid,
      },
      secondary: {
        label: t('briefTemplateCreation.briefTemplateIntakeModal.cancel'),
        onClick: onClose,
      },
    };
  }

  const pickerAddLabel =
    shell.pickerSelectedCount > 0
      ? t('briefTemplateCreation.briefTemplateIntakeModal.addSelected', {
          selectedCount: shell.pickerSelectedCount,
        })
      : t('briefTemplateCreation.briefTemplateIntakeModal.add');

  return {
    title,
    primary: {
      label: pickerAddLabel,
      onClick: shell.handlePickerAdd,
      disabled: shell.pickerSelectedCount === 0,
    },
    secondary: {
      label: t('briefTemplateCreation.briefTemplateIntakeModal.back'),
      onClick: shell.handlePickerBack,
    },
  };
}
